import { useState } from 'react';
import { format, parse, type Locale } from 'date-fns';
import { fr, ar, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from 'lucide-react';
import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '../../lib/utils';

const DATE_FNS_LOCALES: Record<string, Locale> = { fr, ar, en: enUS };

interface DatePickerProps {
  /** ISO date string (YYYY-MM-DD) or empty */
  value: string;
  onChange: (iso: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function DatePicker({
  value,
  onChange,
  placeholder,
  className,
  disabled,
}: DatePickerProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const locale = DATE_FNS_LOCALES[i18n.language] ?? fr;

  const date = value ? parse(value, 'yyyy-MM-dd', new Date()) : undefined;

  const handleSelect = (day: Date | undefined) => {
    if (day) {
      onChange(format(day, 'yyyy-MM-dd'));
    } else {
      onChange('');
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full justify-start text-start font-normal',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon size={16} className="shrink-0 text-muted-foreground" />
          {date ? format(date, 'dd/MM/yyyy', { locale }) : (placeholder ?? t('common.selectDate'))}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleSelect}
          defaultMonth={date}
        />
      </PopoverContent>
    </Popover>
  );
}
